/**
 * useContactSearch
 *
 * Single source of recipient options for the three communication pickers: the
 * Send-via-Email modal, the Communication Log email popover and the phone popover.
 *
 * Two tiers of people, deliberately kept apart:
 *  - Contacts come only from `entities/{communicationLogContactEntityId}`, searched
 *    server-side through Algolia. Every query is scoped by `_parentDocId`, so records
 *    belonging to any other entity can never surface here as duplicate people.
 *  - System users come only from the `users/` collection via `UserContext.users`, and
 *    are matched client-side. They are never resolved as entity records.
 *
 * Replaces the capped login-time contact preload (SYD-2229): there is no 500-record
 * ceiling and no stale snapshot — every keystroke asks the index. The registry cache is
 * deliberately bypassed (`cacheRecords: []`) because cached records carry no `fields`,
 * and the search merge prefers cache over Algolia — using it would strip the very email
 * and phone values this hook exists to read.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { DocDataWithId, DocId, EntityRecord, FieldType, Fields } from '#types';
import { UserContext } from '@/providers/UserContext';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { useEntityRecordSearch } from '@/use/useEntityRecordSearch';
import { fetchRecord } from '@/store/entityRecordsRegistry';

export type ContactKind = 'email' | 'phone';

export type ContactOption = {
    /** antd Select value: the address for `email`, the record docId for `phone` */
    value: string;
    /** The person's name — antd retains it for a committed tag after the option is gone */
    label: string;
    /** The dropdown row: name, source and address */
    detail: string;
    /** Set when the person carries no value of the requested kind */
    disabled?: boolean;
    docId: string;
    type: 'contact' | 'user';
    /** Normalized email address or phone number; absent when the person has none */
    contactValue?: string;
};

type UseContactSearchParams = {
    kind: ContactKind;
    /** Record ids and user uids to resolve up front, independent of the search query */
    includeDocIds?: string[];
};

type UseContactSearchReturn = {
    /** Search-driven options: matching contacts first, then matching system users */
    options: ContactOption[];
    /** Options resolved from `includeDocIds`, independent of the search query */
    includedOptions: ContactOption[];
    loading: boolean;
    search: (query: string) => void;
    /** Display label of the configured contact entity, for field labels and option text */
    contactEntityLabel: string;
};

/** Firestore leaves `docId` optional; these are the shapes after it has been established */
type IdentifiedRecord = EntityRecord & { docId: DocId };
type IdentifiedUser = DocDataWithId & { docId: DocId };

const hasDocId = <T extends { docId?: DocId }>(doc: T): doc is T & { docId: DocId } => Boolean(doc.docId);

type BackfillState = { entityId: string; records: Record<string, IdentifiedRecord> };

const NO_CACHE_RECORDS: EntityRecord[] = [];
const NO_BACKFILL: Record<string, IdentifiedRecord> = {};

const MISSING_VALUE_LABEL: Record<ContactKind, string> = {
    email: 'no email on record',
    phone: 'no phone on record',
};

const normalizeEmailValue = (value?: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.includes('@') ? trimmed : undefined;
};

const normalizePhoneValue = (value?: unknown): string | undefined => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 && /\d/.test(trimmed) ? trimmed : undefined;
    }

    if (value && typeof value === 'object') {
        const phoneObj = value as { formatted?: string; components?: { countryCode?: string; number?: string } };

        if (typeof phoneObj.formatted === 'string' && phoneObj.formatted.trim().length > 0) {
            const trimmed = phoneObj.formatted.trim();
            return /\d/.test(trimmed) ? trimmed : undefined;
        }

        if (phoneObj.components) {
            const { countryCode, number } = phoneObj.components;
            if (typeof number === 'string' && number.trim().length > 0) {
                const fullNumber = countryCode ? `${countryCode}${number}` : number;
                return /\d/.test(fullNumber) ? fullNumber.trim() : undefined;
            }
        }
    }

    return undefined;
};

const normalizeValue = (kind: ContactKind, value?: unknown): string | undefined =>
    kind === 'email' ? normalizeEmailValue(value) : normalizePhoneValue(value);

/**
 * Prefer the conventional field id (`email` / `phone`), then the first field whose
 * registered type matches the kind. Ids are sorted so the fallback is deterministic.
 */
const contactValueFromFields = (
    fields: Fields | undefined,
    kind: ContactKind,
    fieldTypes: Record<string, FieldType>
): string | undefined => {
    if (!fields) {
        return undefined;
    }

    const preferred = normalizeValue(kind, fields[kind]?.value);
    if (preferred) {
        return preferred;
    }

    for (const fieldId of Object.keys(fields).sort()) {
        if (fieldTypes[fieldId]?.fieldDataTypeId !== kind) {
            continue;
        }
        const candidate = normalizeValue(kind, fields[fieldId]?.value);
        if (candidate) {
            return candidate;
        }
    }

    return undefined;
};

const buildOption = (
    docId: string,
    name: string,
    type: ContactOption['type'],
    contactValue: string | undefined,
    kind: ContactKind,
    typeLabel: string
): ContactOption => ({
    value: kind === 'email' ? contactValue ?? docId : docId,
    label: name,
    detail: `${name} • ${typeLabel} • ${contactValue ?? MISSING_VALUE_LABEL[kind]}`,
    disabled: !contactValue,
    docId,
    type,
    contactValue,
});

const contactOptionFromRecord = (
    record: IdentifiedRecord,
    kind: ContactKind,
    fieldTypes: Record<string, FieldType>,
    contactEntityLabel: string
): ContactOption =>
    buildOption(
        record.docId,
        record.name || record.meta?.description?.shortLabel || 'Unnamed Record',
        'contact',
        contactValueFromFields(record.fields, kind, fieldTypes),
        kind,
        contactEntityLabel
    );

const userOptionFromDoc = (userDoc: IdentifiedUser, kind: ContactKind): ContactOption => {
    const contactValue = normalizeValue(kind, userDoc[kind]);
    const nameCandidate = userDoc.displayName ?? userDoc.name ?? normalizeEmailValue(userDoc.email);
    const name =
        typeof nameCandidate === 'string' && nameCandidate.trim().length > 0 ? nameCandidate : 'Unnamed User';

    return buildOption(userDoc.docId, name, 'user', contactValue, kind, 'User');
};

const dedupeOptions = (options: ContactOption[]): ContactOption[] => {
    const seenDocIds = new Set<string>();
    const seenValues = new Set<string>();

    return options.filter((option) => {
        if (seenDocIds.has(option.docId) || seenValues.has(option.value)) {
            return false;
        }
        seenDocIds.add(option.docId);
        seenValues.add(option.value);
        return true;
    });
};

export const useContactSearch = ({ kind, includeDocIds }: UseContactSearchParams): UseContactSearchReturn => {
    const { data: orgData } = useOrganizationParameters();
    const { entities } = useContext(EntitiesContext);
    const { fieldTypes } = useContext(FieldTypeContext);
    const { users } = useContext(UserContext);

    const contactEntityId = orgData?.communicationLogContactEntityId || 'Contacts';
    const contactEntityLabel = entities[contactEntityId]?.description?.shortLabel || contactEntityId;

    const [query, setQuery] = useState('');
    const [backfill, setBackfill] = useState<BackfillState>({ entityId: contactEntityId, records: {} });
    const [includedRecords, setIncludedRecords] = useState<IdentifiedRecord[]>([]);
    const [includedLoading, setIncludedLoading] = useState(false);
    const requestedBackfillRef = useRef({ entityId: contactEntityId, docIds: new Set<string>() });

    /** The entity id flips from the `Contacts` fallback once org parameters land */
    if (requestedBackfillRef.current.entityId !== contactEntityId) {
        requestedBackfillRef.current = { entityId: contactEntityId, docIds: new Set() };
    }
    const backfilledRecords = backfill.entityId === contactEntityId ? backfill.records : NO_BACKFILL;

    const { searchResults, isSearching, search: searchRecords } = useEntityRecordSearch({
        entityId: contactEntityId,
        cacheRecords: NO_CACHE_RECORDS,
    });

    const search = useCallback(
        (nextQuery: string) => {
            setQuery(nextQuery);
            searchRecords(nextQuery);
        },
        [searchRecords]
    );

    /** Algolia truncates oversized records: a hit without `fields` needs a direct read */
    useEffect(() => {
        const requested = requestedBackfillRef.current.docIds;
        const missing = searchResults
            .filter(hasDocId)
            .filter((record) => !record.fields && !requested.has(record.docId));
        if (missing.length === 0) {
            return;
        }

        missing.forEach((record) => requested.add(record.docId));

        void Promise.all(
            missing.map((record) =>
                fetchRecord(contactEntityId, record.docId).catch((error) => {
                    console.warn('[useContactSearch] contact backfill failed:', error);
                    requested.delete(record.docId);
                    return null;
                })
            )
        ).then((records) => {
            const resolved = records.filter((record): record is IdentifiedRecord => Boolean(record?.docId));
            if (resolved.length === 0) {
                return;
            }
            setBackfill((previous) => ({
                entityId: contactEntityId,
                records: {
                    ...(previous.entityId === contactEntityId ? previous.records : {}),
                    ...Object.fromEntries(resolved.map((record) => [record.docId, record])),
                },
            }));
        });
    }, [searchResults, contactEntityId]);

    const includeKey = includeDocIds ? [...includeDocIds].sort().join(',') : '';
    /** Keyed on uids, not the array: `users` gets a new identity on every settings write */
    const userUidKey = useMemo(() => users.map((userDoc) => userDoc.docId).sort().join(','), [users]);

    useEffect(() => {
        const userUids = new Set(userUidKey.split(','));
        const recordIds = includeKey ? includeKey.split(',').filter((docId) => !userUids.has(docId)) : [];
        if (recordIds.length === 0) {
            setIncludedRecords([]);
            return;
        }

        let cancelled = false;
        setIncludedLoading(true);

        void Promise.all(
            recordIds.map((docId) =>
                fetchRecord(contactEntityId, docId).catch((error) => {
                    console.warn('[useContactSearch] form contact resolution failed:', error);
                    return null;
                })
            )
        )
            .then((records) => {
                if (cancelled) return;
                setIncludedRecords(records.filter((record): record is IdentifiedRecord => Boolean(record?.docId)));
            })
            .finally(() => {
                if (!cancelled) setIncludedLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [includeKey, contactEntityId, userUidKey]);

    const userOptions = useMemo(
        () => users.filter(hasDocId).map((userDoc) => userOptionFromDoc(userDoc, kind)),
        [users, kind]
    );

    const options = useMemo(() => {
        const trimmed = query.trim().toLowerCase();

        const contactOptions = trimmed
            ? searchResults
                  .filter(hasDocId)
                  .map((record) =>
                      contactOptionFromRecord(
                          backfilledRecords[record.docId] ?? record,
                          kind,
                          fieldTypes,
                          contactEntityLabel
                      )
                  )
            : [];

        const matchingUsers = trimmed
            ? userOptions.filter(
                  (option) =>
                      option.label.toLowerCase().includes(trimmed) ||
                      option.contactValue?.toLowerCase().includes(trimmed)
              )
            : userOptions;

        return dedupeOptions([...contactOptions, ...matchingUsers]);
    }, [query, searchResults, backfilledRecords, kind, fieldTypes, contactEntityLabel, userOptions]);

    const includedOptions = useMemo(() => {
        if (!includeKey) {
            return [];
        }

        const includedIds = new Set(includeKey.split(','));
        const contactOptions = includedRecords.map((record) =>
            contactOptionFromRecord(record, kind, fieldTypes, contactEntityLabel)
        );
        const includedUsers = userOptions.filter((option) => includedIds.has(option.docId));

        return dedupeOptions([...contactOptions, ...includedUsers]);
    }, [includeKey, includedRecords, kind, fieldTypes, contactEntityLabel, userOptions]);

    return {
        options,
        includedOptions,
        loading: isSearching || includedLoading,
        search,
        contactEntityLabel,
    };
};
